import { NFR_STATUS_VERSION_HEADER, NFR_STATUS_VERSION } from './nfrStatus';
import { handleTenantAccessRevoked, isTenantAccessRevoked } from './tenantAccessRevoked';
import { handleActionSessionOutOfScope, isActionSessionOutOfScope } from './actionSessionOutOfScope';

export const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * #1153 — Structured-error class for non-2xx API responses. Carries the parsed
 * response body so consumers can read `body.nextAction`, `body.state`, etc.
 * without re-parsing message strings. Subclass of Error so existing
 * `err instanceof Error` checks remain valid throughout the codebase.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiFetchOptions extends RequestInit {
  /**
   * #3248 PR 2 — send no Bearer header even when a full login is stored. For a
   * request that is not made AS the stored login (POST /action/redeem acts for
   * the magic link's recipient), so a refusal of it is never treated as a
   * refusal of the stored login: the TENANT_ACCESS_REVOKED handler below then
   * takes its cookie-only path and clears nothing.
   */
  omitBearer?: boolean;
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const { omitBearer, ...options } = opts;
  const token = omitBearer ? null : localStorage.getItem('blueprint_token');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // #2835 deploy 1 — see nfrStatus.ts. Removed in 0235's PR.
    [NFR_STATUS_VERSION_HEADER]: NFR_STATUS_VERSION,
    ...(options.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });

  if (res.status === 402) {
    const body = await res.json().catch(() => ({})) as {
      error?: string;
      currentTier?: string;
      requiredTier?: string;
      upgradeUrl?: string;
      tokensUsed?: number;
      cap?: number;
      resetsAt?: string;
    };
    if (body.error === 'TOKEN_ALLOWANCE_EXHAUSTED') {
      window.dispatchEvent(new CustomEvent('token-exhaustion', { detail: body }));
    } else {
      window.dispatchEvent(new CustomEvent('tier-upgrade-required', { detail: body }));
    }
    throw new ApiError(body.error ?? 'Upgrade required', 402, body as Record<string, unknown>);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as Record<string, unknown>;
    const message = (body.error as string | undefined) ?? `HTTP ${res.status}`;
    // #3229 Story B — workspace access removed: clear credentials and send the
    // user to sign in again (once). Still throws, so callers stop as usual.
    if (isTenantAccessRevoked(res.status, body)) handleTenantAccessRevoked(token);
    // #3248 — a magic-link action session used outside its one action: sign in
    // to continue, keeping the current page as `redirect` (once). Its own
    // handler: 403 + its own code, never the revoked path above.
    else if (isActionSessionOutOfScope(res.status, body)) handleActionSessionOutOfScope();
    throw new ApiError(message, res.status, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}
